import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { Recording, PortalClass } from '../../types/portal';
import { Plus, Edit2, Trash2, X, Check, Eye, EyeOff, Search, ArrowUp, ArrowDown, Bell, BellOff } from 'lucide-react';

const cardClass =
  'bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.05)]';
const inputClass =
  'w-full px-4 py-3 border border-[#ddd6cf] rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none transition-colors duration-200 bg-white';
const primaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-[#970000] text-white hover:bg-[#7a0000] transition-colors duration-200 font-medium text-sm';
const secondaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full border border-[#d9c9bd] text-[#5f5148] hover:bg-[#f8f3ef] transition-colors duration-200 font-medium text-sm';
const dangerBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 font-medium text-sm';

const emptyForm = () => ({
  class_id: '',
  title: '',
  description: '',
  youtube_url: '',
  book: '',
  lesson: '',
  recorded_at: '',
  is_visible: true,
});

function buildAnnouncement(r: { title: string; lesson?: string | null; recorded_at?: string | null; description?: string | null }) {
  const datePart = r.recorded_at
    ? `vom ${new Date(r.recorded_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}`
    : '';

  const title = `Neue Aufzeichnung online${datePart ? ` – ${datePart}` : ''}`;

  const lessonLine = [r.title?.trim(), r.lesson?.trim()].filter(Boolean).join(' – ');
  const lines = [
    `Die Aufzeichnung des Unterrichts${datePart ? ` ${datePart}` : ''} ist jetzt online.`,
    lessonLine ? `\nFolgender Unterricht ist online:\n${lessonLine}` : '',
    r.description?.trim() ?? '',
  ].filter(Boolean);

  return { title, content: lines.join('\n') };
}

const RecordingsManager: React.FC = () => {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [sortMode, setSortMode] = useState<'manual' | 'newest' | 'oldest'>('manual');
  const [reordering, setReordering] = useState(false);
  const [opError, setOpError] = useState('');
  // Set of recording IDs that already have an announcement
  const [announcedIds, setAnnouncedIds] = useState<Set<string>>(new Set());
  const [sendingId, setSendingId] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    const [{ data: rData }, { data: cData }, { data: aData }] = await Promise.all([
      supabase
        .from('recordings')
        .select('*')
        .order('class_id', { ascending: true })
        .order('sort_order', { ascending: true })
        .order('recorded_at', { ascending: true }),
      supabase.from('portal_classes').select('*').eq('is_active', true).order('title'),
      supabase.from('announcements').select('recording_id').not('recording_id', 'is', null),
    ]);
    setRecordings((rData ?? []) as Recording[]);
    setClasses((cData ?? []) as PortalClass[]);
    const ids = new Set((aData ?? []).map((a: { recording_id: string }) => a.recording_id));
    setAnnouncedIds(ids);
    setLoading(false);
  };

  const classMap = Object.fromEntries(classes.map(c => [c.id, c.title]));

  // Manuelle Reihenfolge innerhalb einer Klasse; Datum als Tie-Breaker
  // (Alt-Daten haben teils alle sort_order = 0)
  const manualCompare = (a: Recording, b: Recording) => {
    if (a.class_id !== b.class_id) return a.class_id < b.class_id ? -1 : 1;
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return (a.recorded_at ?? '').localeCompare(b.recorded_at ?? '');
  };

  const dateKey = (r: Recording) => r.recorded_at ?? r.created_at ?? '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = recordings.filter(r => {
      if (classFilter && r.class_id !== classFilter) return false;
      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        (r.book ?? '').toLowerCase().includes(q) ||
        (r.lesson ?? '').toLowerCase().includes(q)
      );
    });
    if (sortMode === 'newest') return [...list].sort((a, b) => dateKey(b).localeCompare(dateKey(a)));
    if (sortMode === 'oldest') return [...list].sort((a, b) => dateKey(a).localeCompare(dateKey(b)));
    return [...list].sort(manualCompare);
  }, [recordings, search, classFilter, sortMode]);

  const canReorder = !!classFilter && !search.trim() && sortMode === 'manual';

  const move = async (rec: Recording, direction: -1 | 1) => {
    if (reordering) return;
    const siblings = recordings
      .filter(r => r.class_id === rec.class_id)
      .sort(manualCompare);
    const idx = siblings.findIndex(r => r.id === rec.id);
    const target = idx + direction;
    if (target < 0 || target >= siblings.length) return;

    const next = [...siblings];
    [next[idx], next[target]] = [next[target], next[idx]];

    // Sequentiell neu nummerieren — repariert dabei auch Alt-Daten,
    // bei denen alle Aufzeichnungen sort_order 0 hatten (Tausch wirkungslos)
    const renumbered = next.map((r, i) => ({ ...r, sort_order: i + 1 }));
    const changed = renumbered.filter(
      r => siblings.find(s => s.id === r.id)?.sort_order !== r.sort_order
    );

    setReordering(true);
    setRecordings(prev => prev.map(r => renumbered.find(n => n.id === r.id) ?? r));
    try {
      const results = await Promise.all(
        changed.map(r =>
          supabase.from('recordings').update({ sort_order: r.sort_order, updated_at: new Date().toISOString() }).eq('id', r.id)
        )
      );
      const failed = results.find(res => res.error);
      if (failed?.error) throw failed.error;
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Reihenfolge konnte nicht gespeichert werden. Bitte erneut versuchen.');
      load();
    } finally {
      setReordering(false);
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (r: Recording) => {
    setEditId(r.id);
    setForm({
      class_id: r.class_id,
      title: r.title,
      description: r.description ?? '',
      youtube_url: r.youtube_url,
      book: r.book ?? '',
      lesson: r.lesson ?? '',
      recorded_at: r.recorded_at ?? '',
      is_visible: r.is_visible,
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.class_id || !form.title.trim() || !form.youtube_url.trim()) return;
    setSaving(true);
    try {
      const payload = {
        class_id: form.class_id,
        title: form.title.trim(),
        description: form.description.trim() || null,
        youtube_url: form.youtube_url.trim(),
        book: form.book.trim() || null,
        lesson: form.lesson.trim() || null,
        recorded_at: form.recorded_at || null,
        is_visible: form.is_visible,
        updated_at: new Date().toISOString(),
      };

      if (editId) {
        const { error } = await supabase.from('recordings').update(payload).eq('id', editId);
        if (error) throw error;
        setRecordings(prev => prev.map(r => (r.id === editId ? { ...r, ...payload } : r)));

        // Falls bereits eine Ankündigung existiert, mitaktualisieren
        if (announcedIds.has(editId)) {
          const { title: annTitle, content: annContent } = buildAnnouncement(payload);
          await supabase
            .from('announcements')
            .update({ title: annTitle, content: annContent, updated_at: new Date().toISOString() })
            .eq('recording_id', editId);
        }
      } else {
        const maxOrder = recordings
          .filter(r => r.class_id === form.class_id)
          .reduce((m, r) => Math.max(m, r.sort_order), 0);
        const { data, error } = await supabase
          .from('recordings')
          .insert({ ...payload, sort_order: maxOrder + 1 })
          .select()
          .single();
        if (error) throw error;
        setRecordings(prev => [...prev, data as Recording]);
      }
      setShowForm(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Fehler beim Speichern. Bitte erneut versuchen.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('recordings').delete().eq('id', id);
    if (!error) {
      setRecordings(prev => prev.filter(r => r.id !== id));
      setAnnouncedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
    setConfirmDelete(null);
  };

  const toggleVisible = async (r: Recording) => {
    const { error } = await supabase
      .from('recordings')
      .update({ is_visible: !r.is_visible, updated_at: new Date().toISOString() })
      .eq('id', r.id);
    if (!error) {
      setRecordings(prev => prev.map(rec => (rec.id === r.id ? { ...rec, is_visible: !r.is_visible } : rec)));
    }
  };

  const sendAnnouncement = async (r: Recording) => {
    if (announcedIds.has(r.id)) return;
    setSendingId(r.id);
    try {
      const { title, content } = buildAnnouncement(r);
      const { error } = await supabase.from('announcements').insert({
        class_id: r.class_id,
        recording_id: r.id,
        title,
        content,
        is_important: false,
      });
      if (error) throw error;
      setAnnouncedIds(prev => new Set(prev).add(r.id));
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Ankündigung konnte nicht gesendet werden.');
    } finally {
      setSendingId(null);
    }
  };

  const f = (field: keyof ReturnType<typeof emptyForm>) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-1">
            Portal
          </p>
          <h2 className="text-3xl font-bold text-[#1f1f1f]">Aufzeichnungen</h2>
        </div>
        <button onClick={openCreate} className={primaryBtn}>
          <Plus className="h-4 w-4 mr-1" />
          Hinzufügen
        </button>
      </div>

      {opError && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{opError}</p>
          <button onClick={() => setOpError('')} className="text-red-400 hover:text-red-600 text-xs font-medium shrink-0">Schließen</button>
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#eadfd6]">
              <h3 className="text-lg font-bold text-[#1f1f1f]">
                {editId ? 'Aufzeichnung bearbeiten' : 'Neue Aufzeichnung'}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-xl hover:bg-[#f8f3ef]">
                <X className="h-5 w-5 text-[#6b6b6b]" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Klasse *</label>
                <select value={form.class_id} onChange={f('class_id')} className={inputClass} required>
                  <option value="">Klasse wählen…</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Titel *</label>
                <input type="text" value={form.title} onChange={f('title')} className={inputClass} required placeholder="Titel der Aufzeichnung" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">YouTube-URL *</label>
                <input type="url" value={form.youtube_url} onChange={f('youtube_url')} className={inputClass} required placeholder="https://youtu.be/…" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Beschreibung</label>
                <textarea value={form.description} onChange={f('description')} className={inputClass + ' resize-none'} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Buch</label>
                  <input type="text" value={form.book} onChange={f('book')} className={inputClass} placeholder="z.B. Arabiyya" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Lektion</label>
                  <input type="text" value={form.lesson} onChange={f('lesson')} className={inputClass} placeholder="z.B. Lektion 3" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Datum</label>
                <input type="date" value={form.recorded_at} onChange={f('recorded_at')} className={inputClass} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_visible} onChange={e => setForm(p => ({ ...p, is_visible: e.target.checked }))} className="rounded" />
                <span className="text-sm font-medium">Sichtbar für Schüler</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className={primaryBtn}>
                  <Check className="h-4 w-4 mr-1" />
                  {saving ? 'Speichert…' : 'Speichern'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className={secondaryBtn}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] p-6 w-full max-w-sm">
            <p className="font-semibold text-[#1f1f1f] mb-4">Aufzeichnung wirklich löschen?</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(confirmDelete)} className={dangerBtn}>Löschen</button>
              <button onClick={() => setConfirmDelete(null)} className={secondaryBtn}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Suche + Klasse + Sortierung */}
      <div className={cardClass + ' p-4 grid md:grid-cols-3 gap-4'}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9b9490]" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Aufzeichnung suchen…"
            className={inputClass + ' pl-10'}
          />
        </div>
        <select value={classFilter} onChange={e => setClassFilter(e.target.value)} className={inputClass}>
          <option value="">Alle Klassen</option>
          {classes.map(c => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
        <select
          value={sortMode}
          onChange={e => setSortMode(e.target.value as 'manual' | 'newest' | 'oldest')}
          className={inputClass}
        >
          <option value="manual">Manuelle Reihenfolge</option>
          <option value="newest">Letzter Unterricht zuerst</option>
          <option value="oldest">Erster Unterricht zuerst</option>
        </select>
      </div>

      {canReorder ? (
        <p className="text-xs text-[#8a8178] -mt-2">
          Mit den Pfeilen kannst du die Reihenfolge der Lektionen ändern.
        </p>
      ) : (
        <p className="text-xs text-[#8a8178] -mt-2">
          Zum manuellen Sortieren: eine einzelne Klasse wählen, Sortierung auf „Manuelle Reihenfolge“, kein Suchbegriff.
        </p>
      )}

      {/* List */}
      <div className={cardClass + ' overflow-hidden'}>
        {loading ? (
          <div className="p-10 text-center text-[#6b6b6b]">Wird geladen…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#6b6b6b]">Keine Aufzeichnungen gefunden.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eadfd6]">
                  {canReorder && <th className="text-left p-4 font-semibold text-[#6b6b6b]">Sortierung</th>}
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Titel</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Klasse</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Datum</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Sichtbar</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const announced = announcedIds.has(r.id);
                  const sending = sendingId === r.id;
                  return (
                    <tr key={r.id} className="border-b border-[#eadfd6] hover:bg-[#faf8f6]">
                      {canReorder && (
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => move(r, -1)}
                              disabled={idx === 0 || reordering}
                              className="p-1.5 rounded-lg border border-[#ddd6cf] text-[#5f5148] hover:bg-[#f8f3ef] disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Nach oben"
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => move(r, 1)}
                              disabled={idx === filtered.length - 1 || reordering}
                              className="p-1.5 rounded-lg border border-[#ddd6cf] text-[#5f5148] hover:bg-[#f8f3ef] disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Nach unten"
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                      <td className="p-4">
                        <p className="font-medium text-[#1f1f1f]">{r.title}</p>
                        {(r.book || r.lesson) && (
                          <p className="text-xs text-[#6b6b6b] mt-0.5">
                            {[r.book, r.lesson].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </td>
                      <td className="p-4 text-[#6b6b6b]">{classMap[r.class_id] ?? '–'}</td>
                      <td className="p-4 text-[#6b6b6b]">
                        {r.recorded_at ? new Date(r.recorded_at).toLocaleDateString('de-DE') : '–'}
                      </td>
                      <td className="p-4">
                        <button onClick={() => toggleVisible(r)} title={r.is_visible ? 'Ausblenden' : 'Einblenden'}>
                          {r.is_visible ? (
                            <Eye className="h-4 w-4 text-green-600" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-[#9b9490]" />
                          )}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => openEdit(r)} className={secondaryBtn}>
                            <Edit2 className="h-3.5 w-3.5 mr-1" />
                            Bearbeiten
                          </button>
                          {announced ? (
                            <span className="inline-flex items-center px-3 py-2 rounded-full border border-green-200 bg-green-50 text-green-700 text-sm font-medium gap-1.5 cursor-default">
                              <Bell className="h-3.5 w-3.5" />
                              Gesendet
                            </span>
                          ) : (
                            <button
                              onClick={() => sendAnnouncement(r)}
                              disabled={sending}
                              className="inline-flex items-center px-3 py-2 rounded-full border border-[#970000]/30 bg-[#970000]/5 text-[#970000] hover:bg-[#970000]/10 transition-colors text-sm font-medium gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <BellOff className="h-3.5 w-3.5" />
                              {sending ? 'Wird gesendet…' : 'Ankündigung senden'}
                            </button>
                          )}
                          <button onClick={() => setConfirmDelete(r.id)} className="p-2 rounded-xl text-red-500 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingsManager;
